import { Box, Theme, Typography, styled, useTheme } from '@mui/material';
import React, { useState } from 'react';
import TotalCost from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/PayConstantsEntryForm/TotalCost';
import StyIconChip from 'routes/HumanResourcesModule/components/ui/StyIconChip';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import { usePrimaryColor } from 'styles/theme';
import FreeHypothesisCreationModal from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/FreeHypothesisCreationModal';
import Flex from 'ui/Flex/Flex';
import EditIcon from '@mui/icons-material/Edit';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import {
    getStartMonth,
    getEndMonth,
    getFreeHypothesisLabel,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/dataHelpers';
import {
    FreeHypothesis,
    FreeHypothesisEditMutationParams,
    FreeHypothesisMutationParams,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';
import StyChip from 'routes/HumanResourcesModule/components/ui/StyChip';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import FreeHypothesisEditModal from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/FreeHypothesisEditModal';
import { useTranslation } from 'react-i18next';
import { CostPerYearT } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/GVTData/helpers/hooks/gvtData';

const ChipHypothesisSelector = ({
    freeHypotheses,
    primaryColor,
    theme,
    setOpenEditModal,
}: {
    freeHypotheses: FreeHypothesis[];
    primaryColor: string;
    theme: Theme;
    setOpenEditModal: (value: boolean) => void;
}): React.ReactElement => {
    const { t } = useTranslation();
    return (
        <>
            {freeHypotheses.length > 1 ? (
                <StyIconChip
                    icon={<EditIcon />}
                    label={t(
                        'humanResources.simulation.freeHypothesis.seeMoreHypotheses',
                        { hypothesesNumber: freeHypotheses.length },
                    )}
                    styledColor={primaryColor}
                    sx={{
                        gap: '10px',
                        backgroundColor: `${theme.palette.common.white}`,
                    }}
                    onClick={() => setOpenEditModal(true)}
                />
            ) : (
                <StyChip
                    icon={<EditIcon />}
                    styledColor=""
                    label={getFreeHypothesisLabel(freeHypotheses[0].filters)}
                    sx={{
                        maxWidth: '130px',
                        backgroundColor: `${theme.palette.common.white}`,
                    }}
                    onClick={() => setOpenEditModal(true)}
                />
            )}
        </>
    );
};

const StyBlock = styled(Box, {
    name: 'StyBlock',
})(() => ({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'transparent',

    padding: '12px',
    textAlign: 'left',
    gap: '20px',
    height: '100%',
}));

type Props = {
    freeHypotheses: FreeHypothesis[];
    hrSimulation: HrSimulation;
    freeHypothesisTotal: Record<string, number>;
    createFreeHypothesis: (payload: FreeHypothesisMutationParams) => void;
    setFreeHypothesisUpdatePayload: (
        payload: FreeHypothesisEditMutationParams,
    ) => void;
    hasChange: () => void;
    freeHypothesisPerYear: Record<string, CostPerYearT>;
};

const FreeHypothesesDisplay = ({
    freeHypotheses,
    hrSimulation,
    freeHypothesisTotal,
    freeHypothesisPerYear,
    setFreeHypothesisUpdatePayload,
    createFreeHypothesis,
    hasChange,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const primaryColor: string = usePrimaryColor();
    const [openCreationModal, setOpenCreationModal] = useState<boolean>(false);
    const [openEditModal, setOpenEditModal] = useState<boolean>(false);
    const theme = useTheme();
    const handleModal = () => {
        setOpenCreationModal(!openCreationModal);
    };
    const freeHypothesisTotalCost = Object.values(freeHypothesisTotal).reduce(
        (acc, curr) => acc + curr,
        0,
    );
    const freeHypothesisPerYearCost: CostPerYearT = Object.values(
        freeHypothesisPerYear,
    ).reduce((acc: CostPerYearT, curr: CostPerYearT) => {
        const result: CostPerYearT = { ...acc };
        Object.entries(curr).forEach(([year, cost]) => {
            const yearNum = Number(year);
            result[yearNum] = (result[yearNum] || 0) + cost;
        });
        return result;
    }, {});

    return (
        <StyBlock>
            <Typography variant="h4" sx={{ width: '100%', fontSize: '16px' }}>
                {PayComponent.IFSE_AND_MONTHLY_PRIMES}
            </Typography>
            <Typography variant="body1">
                {t('humanResources.simulation.freeHypothesis.hypotheses')}
            </Typography>
            <Flex gap="12px" sx={{ width: '100%' }}>
                <ChipHypothesisSelector
                    freeHypotheses={freeHypotheses}
                    primaryColor={primaryColor}
                    theme={theme}
                    setOpenEditModal={setOpenEditModal}
                />

                <StyIconChip
                    onClick={handleModal}
                    icon={<AddCircleOutlinedIcon />}
                    styledColor={primaryColor}
                />
            </Flex>

            <TotalCost
                label={t(
                    'humanResources.simulation.freeHypothesis.ifse.totalCostLabel',
                    {
                        startMonth: getStartMonth(hrSimulation.startDate),
                        endMonth: getEndMonth(hrSimulation.endDate),
                    },
                )}
                totalCost={freeHypothesisTotalCost}
                costPerYear={freeHypothesisPerYearCost}
                isLoading={false}
            />
            {openCreationModal && (
                <FreeHypothesisCreationModal
                    show={openCreationModal}
                    onClose={() => {
                        setOpenCreationModal(false);
                    }}
                    hrSimulation={hrSimulation}
                    createFreeHypothesis={createFreeHypothesis}
                    hasChange={hasChange}
                />
            )}
            {openEditModal && (
                <FreeHypothesisEditModal
                    show={openEditModal}
                    onClose={() => {
                        setOpenEditModal(false);
                    }}
                    freeHypotheses={freeHypotheses}
                    hrSimulation={hrSimulation}
                    freeHypothesisTotals={freeHypothesisTotal}
                    hasChange={hasChange}
                    setFreeHypothesisUpdatePayload={
                        setFreeHypothesisUpdatePayload
                    }
                />
            )}
        </StyBlock>
    );
};

export default FreeHypothesesDisplay;
